import React, { useCallback, useMemo } from 'react';
import { useLaborCostDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/LaborCostDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

const COLUMNS: DataTableColumn[] = [
  { key: 'weekStartDate', label: 'Week Start Date', width: '12%', noWrap: true },
  { key: 'location', label: 'Location', sortable: true, width: '23%' },
  { key: 'teamMember', label: 'Team Member', sortable: true, width: '23%' },
  {
    key: 'totalWeeklyWorkingHours',
    label: 'Total Weekly Working Hours',
    align: 'center',
    width: '14%',
  },
  { key: 'weeklyLimit', label: 'Weekly Limit', align: 'center', width: '14%' },
  { key: 'weeklyOvertime', label: 'Weekly Overtime', align: 'center', width: '14%' },
];

export const WeeklyOvertimeTable: React.FC = () => {
  const data = useLaborCostDashboardStore((s) => s.weeklyOvertimeDetails);
  const currentPage = useLaborCostDashboardStore((s) => s.weeklyOvertimeDetailsPage);
  const totalPages = useLaborCostDashboardStore((s) => s.weeklyOvertimeDetailsTotalPages);
  const isLoading = useLaborCostDashboardStore((s) => s.weeklyOvertimeDetailsLoading);
  const hasError = useLaborCostDashboardStore((s) => s.weeklyOvertimeDetailsError);
  const fetchWeeklyOvertimeDetails = useLaborCostDashboardStore(
    (s) => s.fetchWeeklyOvertimeDetails,
  );

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchWeeklyOvertimeDetails(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchWeeklyOvertimeDetails, dateRange, locationIds, positions, employmentTypes],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        weekStartDate: d.weekStartDate,
        location: d.location,
        teamMember: d.teamMember,
        totalWeeklyWorkingHours: d.totalWeeklyWorkingHours,
        weeklyLimit: d.weeklyLimit,
        weeklyOvertime: d.weeklyOvertime,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return <EmptyStateContainer>Failed to load weekly overtime data</EmptyStateContainer>;
  }

  if (!data && !isLoading) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      ariaLabel="Weekly overtime details table"
      fixedLayout
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
